import { CommonModule } from '@angular/common';
import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { StudentSearchComponent } from 'Src/ng2/student/navigation/student-search/student-search.component';
import { StudentSearchModule } from 'Src/ng2/student/navigation/student-search/student-search.module';
import { INavStateConfig, NavStateService } from '../../layouts/services/nav-state/nav-state.service';
import { ActivatedRoute, Router } from '@angular/router';
import { map as rxMap, Observable, of, take, combineLatest, filter as rxFilter, Subscription } from 'rxjs';
import { NvIconButtonModule } from 'projects/shared/nvps-libraries/design/nv-icon-button/nv-icon-button.module';
import { NvAriaLabelModule } from 'projects/shared/nvps-libraries/design/directives/nv-aria-label/nv-aria-label.module';
import { UrlNavigationService } from '../../services/url-navigation/url-navigation.service';
import { GlobalSearchDataService, TSearchOptions } from './global-search-data.service';
import { TOpEntityType } from '../../typings/types/entity-type.types';
import { ENTITY_MODE, TEntityMode } from '../../typings/types/entity-mode.types';
import { getLabel } from '../../services/picker/picker.text.service';
import { EntitySearchComponent, IEntitySearchOption } from '../entity-search/entity-search.component';
import { ImUser } from '../../services/im-models/im-user';
import { getCurrentUser } from '../../../store';
import { IUser } from '../../typings/interfaces/user.interface';
import { SharedEventTrackers } from '../../services/mixpanel/event-trackers/shared-tracking.service';
import { SEARCH_ACTION_TO_EVENT_TYPE, SEARCH_ACTIONS, SearchDestinationPage, SearchSourceElement, TSearchAction, TSearchActionDetails } from '../../services/mixpanel/event-interfaces/search-action';
import { getPageClassificationFromRoute } from '../../helpers/page-classification/page-classification.helper';
import { StudentProfileLoadNotificationService } from '../../services/student-profile-load-notification/student-profile-load-notification.service';
import { ObjectCache } from '../../services/object-cache/object-cache.service';
import { SessionStorageService } from '../../services/web-storage/session-storage/session-storage.service';
import { TDistrictId } from '../../typings/interfaces/district.interface';

export type TGlobalSearchTarget = 'singleEntity' | 'multiEntity';
export interface ISearchPerformed {
  searchTextLength: number;
  searchResultCount: number;
}
export interface ISearchConfig {
  placeholder: string;
  isDisabled: boolean;
}

export type ISearchConfigs = Record<TGlobalSearchTarget, ISearchConfig>;

export interface IGlobalSearchViewModel {
  searchOptions: TSearchOptions;
  entityOptions: IEntitySearchOption[];
  searchConfig: ISearchConfig;
  searchState: INavStateConfig['search'];
}

/**
 * GlobalSearchAdapterComponent — thin wrapper around student-search.
 *
 * This component is intentionally a pass-through today so that in the future
 * the search surface can be expanded beyond just student search (e.g. school
 * search, staff search) without changing any call-sites.  All routing and
 * navigation logic remains inside the wrapped components.
 */
@Component({
  selector: 'global-search-adapter',
  templateUrl: './global-search-adapter.component.html',
  styleUrls: ['./global-search-adapter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, StudentSearchModule, EntitySearchComponent, NvIconButtonModule, NvAriaLabelModule],
})
export class GlobalSearchAdapterComponent {
  @ViewChild(StudentSearchComponent) private studentSearch: StudentSearchComponent;

  public activeTarget: TGlobalSearchTarget = 'singleEntity';

  public entityId: string;
  public entityType: TOpEntityType;
  public entityMode: TEntityMode;
  public networkId: string | null;
  public subEntityId: string | null;

  public navStateConfig$: Observable<INavStateConfig>;
  // search adapter pass-through inputs
  public studentSearchOptions$: Observable<TSearchOptions>;
  public vm$: Observable<IGlobalSearchViewModel>;

  public searchConfigs: ISearchConfigs = {
    singleEntity: { placeholder: 'Jump to a student profile', isDisabled: false },
    multiEntity: { placeholder: 'Jump to a student or [entity type] profile', isDisabled: false },
  };

  private awaitingProfileLoadToClearSearch = false;
  private profileLoadedSub: Subscription;

  constructor (
    private navStateService: NavStateService,
    private globalSearchData: GlobalSearchDataService,
    private route: ActivatedRoute,
    private router: Router,
    private urlNavigationService: UrlNavigationService,
    private store: Store<any>,
    private imUser: ImUser,
    private sharedEventTrackers: SharedEventTrackers,
    private studentProfileLoadNotificationService: StudentProfileLoadNotificationService,
    private objectCache: ObjectCache,
    private sessionStorageService: SessionStorageService,
  ) {}

  ngOnDestroy (): void {
    this.profileLoadedSub?.unsubscribe();
  }

  get searchConfig (): ISearchConfig {
    return this.searchConfigs[this.activeTarget];
  }

  ngOnInit (): void {
    this.resolveContext();
    this.vm$ = this.initializeViewModel();
    this.profileLoadedSub = this.listenForProfileLoadToClearSearch();
  }

  /**
   * A pick leaves the student's name in the box as feedback while the profile loads (the
   * textbox writes it on selection); empty the box once the picked profile lands so it is
   * ready for the next search. The picked and loaded ids can differ in representation across
   * entity types, and every pick funnels into exactly one profile load — so the next load
   * event is the pick's completion.
   */
  private listenForProfileLoadToClearSearch (): Subscription {
    return this.studentProfileLoadNotificationService
      .getStudentProfileLoadedMessage()
      .subscribe(() => {
        if (!this.awaitingProfileLoadToClearSearch) return;
        this.awaitingProfileLoadToClearSearch = false;
        this.studentSearch?.resetSearch();
      });
  }

  private resolveContext (): void {
    const { entityId, entityType, entityMode, networkId, subEntityId } = UrlNavigationService.getOpContext(this.route.snapshot);
    this.entityId = entityId;
    this.entityType = entityType as TOpEntityType;
    this.entityMode = entityMode;
    this.networkId = networkId;
    this.subEntityId = subEntityId;
    this.activeTarget = entityMode === ENTITY_MODE.MULTI_ENTITY ? 'multiEntity' : 'singleEntity';
    this.searchConfigs.multiEntity.placeholder = `Jump to a ${getLabel(this.entityType)} profile`;
  }

  private initializeViewModel (): Observable<IGlobalSearchViewModel> {
    this.navStateConfig$ = this.navStateService.getLatestConfig$();
    const searchOptions$ = this.activeTarget === 'multiEntity' ? of([] as TSearchOptions) : this.getStudentOptions$();
    const entityOptions$ = this.activeTarget === 'multiEntity' ? this.getEntityOptions$() : of([] as IEntitySearchOption[]);
    return combineLatest([searchOptions$, entityOptions$, this.navStateConfig$]).pipe(
      rxMap(([searchOptions, entityOptions, navStateConfig]) => {
        return {
          searchOptions,
          entityOptions,
          searchConfig: this.searchConfig,
          searchState: navStateConfig.search,
        };
      }),
    );
  }

  // Owned by GlobalSearchDataService so it survives the shell rebuild a sibling navigation causes.
  private getStudentOptions$ (): Observable<TSearchOptions> {
    this.studentSearchOptions$ = this.globalSearchData.getStudentSearchOptions$({
      context: {
        entityType: this.entityType,
        entityId: this.entityId,
        entityMode: this.entityMode,
        networkId: this.networkId,
        subEntityId: this.subEntityId,
        districtId: this.sessionStorageService.getItem('currentDistrict') as TDistrictId,
      },
    });
    return this.studentSearchOptions$;
  }

  // Sources the multi-entity (network) school/shelter profiles the current user can access.
  // Reads from the same `userScopesEntityTypeMap` store slice as the profile view so the
  // emitted entityId matches the profile the route resolves to.
  private getEntityOptions$ (): Observable<IEntitySearchOption[]> {
    return this.store.select(getCurrentUser).pipe(
      rxFilter((user: IUser) => !!user?.userScopesEntityTypeMap),
      take(1),
      rxMap((user: IUser) => this.imUser.getScopesBasedOnEntityType(user.userScopesEntityTypeMap, this.entityType)
        .filter(scope => !!scope.entityId)
        .map(scope => ({
          entityId: scope.entityId,
          entityName: scope.entityName,
          entityNickName: scope.entityNickName,
        }))),
    );
  }

  public onResultSelected ($result: string, activeTarget: TGlobalSearchTarget): void {
    switch (activeTarget) {
      case 'singleEntity':
        this.onStudentResultSelected($result);
        break;
      case 'multiEntity':
        this.onEntityResultSelected($result);
        break;
      default:
        break;
    }
  }

  private onStudentResultSelected (studentId: string): void {
    this.trackSearchEvent(SEARCH_ACTIONS.SELECTED_STUDENT_RESULT, {
      searchDestinationPage: SearchDestinationPage.Profile,
    });
    this.awaitingProfileLoadToClearSearch = true;
    this.urlNavigationService.navigateToStudentProfile({
      studentId,
      entityId: this.entityId,
      entityType: this.entityType,
    });
    this.clearNowWhenPickingCurrentStudent(studentId);
  }

  /**
   * Picking the student already on screen produces no profile load, so the loaded event above
   * never arrives and the name would sit in the box indefinitely. The current student's id — in
   * the same id space as picks (shelter picks are CARES ids; the loaded event is not) — lives in
   * the URL's stamped filter hash, so resolve it and clear right away on a match.
   */
  private async clearNowWhenPickingCurrentStudent (studentId: string): Promise<void> {
    const filter = await this.objectCache.getObject(this.currentRouteFilterHash());
    const currentStudentId = filter?.currentStudentId ?? (Array.isArray(filter?._id) ? filter._id[0] : filter?._id);
    if (!currentStudentId || currentStudentId !== studentId) return;
    this.awaitingProfileLoadToClearSearch = false;
    this.studentSearch?.resetSearch();
  }

  // Read synchronously at pick time, before the navigation replaces the URL.
  private currentRouteFilterHash (): string | null {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) snapshot = snapshot.firstChild;
    return snapshot.queryParams?.filter ?? null;
  }

  private onEntityResultSelected (entityId: string): void {
    this.trackSearchEvent(SEARCH_ACTIONS.SELECTED_ENTITY_RESULT, {
      searchDestinationPage: SearchDestinationPage.Profile,
    });
    this.urlNavigationService.navigateToEntityProfile({
      entityType: this.entityType,
      entityId,
      networkId: this.networkId,
    });
  }

  // Handler helper
  public toggleSearchMode (): void {
    this.navStateService.toggleSearchMode();
  }

  private trackSearchEvent (searchAction: TSearchAction, details: TSearchActionDetails = {}): void {
    const { featureSet, pageTemplate } = getPageClassificationFromRoute(this.router.routerState.snapshot.root);
    this.sharedEventTrackers.trackSearchAction({
      action: SEARCH_ACTION_TO_EVENT_TYPE[searchAction],
      searchAction,
      featureSet,
      pageTemplate,
      searchSourceElement: SearchSourceElement.GlobalHeader,
      ...details,
    });
  }
}
